import { get, set } from "lodash";

import {
    iconTransformsInitialState,
    globalIconTransformsInitialState,
    iconEditorInitialState
} from "./state";

import { SHAPES, DEFAULT_SHAPE_COLOR } from "icons/IconEditor/constants";

import { normalizeHexCode } from "icons/IconEditor/helpers";
import { iconIsEdited } from "js/contexts/IconEditorContext/helpers";

export const setEditorPreview = ({ state, iconId, editorPreviewHtml }) => {
    const svgDataUri = `data:image/svg+xml;base64,${btoa(
        unescape(encodeURIComponent(editorPreviewHtml))
    )}`;

    const currentIconTransformsState = state[iconId];

    const newIconTransformsState = {
        ...(currentIconTransformsState || iconTransformsInitialState),
        editorPreview: svgDataUri
    };

    return {
        ...state,
        [iconId]: { ...newIconTransformsState }
    };
};

const rotateClockwise = ({ currentRotation }) => {
    if (currentRotation === 345) return 0;
    return currentRotation + 15;
};

const rotateCounterclockwise = ({ currentRotation }) => {
    if (currentRotation === 0) return 345;
    return currentRotation - 15;
};

export const rotate = ({ state, iconId, direction }) => {
    const currentRotation = get(
        state,
        [iconId, "rotation"],
        iconTransformsInitialState.rotation
    );

    // if we've flipped x or y (but not both), reverse the rotate direction
    if (
        state[iconId] &&
        Boolean(state[iconId].flipX) !== Boolean(state[iconId].flipY)
    ) {
        direction = direction === "cw" ? "ccw" : "cw";
    }

    let newRotation;

    if (direction === "cw")
        newRotation = rotateClockwise({
            currentRotation
        });

    if (direction === "ccw")
        newRotation = rotateCounterclockwise({
            currentRotation
        });

    const currentIconTransformsState = state[iconId];

    const newIconTransformsState = {
        ...(currentIconTransformsState || iconTransformsInitialState),
        rotation: newRotation
    };

    return {
        ...state,
        [iconId]: { ...newIconTransformsState }
    };
};

export const flip = ({ state, axis, iconId }) => {
    let key;

    if (axis === "x") key = "flipX";
    if (axis === "y") key = "flipY";

    const currentIconTransformsState = state[iconId];

    const newIconTransformsState = {
        ...(currentIconTransformsState || iconTransformsInitialState),
        [key]: !get(state, [iconId, key], iconTransformsInitialState[key])
    };

    return {
        ...state,
        [iconId]: { ...newIconTransformsState }
    };
};

export const addToRecentColors = ({ isBackgroundColor, colorToAdd, state }) => {
    const key = isBackgroundColor ? "backgroundColors" : "iconColors";
    const normalizedColorToAdd = normalizeHexCode({ hexCode: colorToAdd });

    const recentColors = [...state[key].recent];
    const notInRecentColors = recentColors.indexOf(normalizedColorToAdd) === -1;
    const RECENT_COLORS_MAX_LENGTH = 10;

    if (recentColors.length === RECENT_COLORS_MAX_LENGTH && notInRecentColors) {
        recentColors.pop();
    }

    return {
        ...state,
        [key]: {
            ...state[key],
            recent: [
                normalizedColorToAdd,
                ...recentColors.filter(color => color !== normalizedColorToAdd)
            ]
        }
    };
};

export const setRecentColors = ({
    areBackgroundColors,
    colors = [],
    state
}) => {
    const key = areBackgroundColors ? "backgroundColors" : "iconColors";
    const normalizedColors = colors.map(color =>
        normalizeHexCode({ hexCode: color })
    );

    return {
        ...state,
        [key]: {
            ...state[key],
            recent: normalizedColors
        }
    };
};

export const resetEditor = ({ state }) => {
    let newState = {
        ...state,
        ...globalIconTransformsInitialState,
        [state.currentIconId]: {
            ...iconTransformsInitialState
        }
    };

    // retain color history palettes
    const pathToIconColorHistory = "iconColors.recent";
    const pathToBackgroundColorHistory = "backgroundColors.recent";

    const currentIconColorHistory = get(state, pathToIconColorHistory);
    const currentBackgroundColorHistory = get(
        state,
        pathToBackgroundColorHistory
    );

    set(newState, pathToIconColorHistory, currentIconColorHistory);
    set(newState, pathToBackgroundColorHistory, currentBackgroundColorHistory);

    // add default colors to color history
    newState = addToRecentColors({
        isBackgroundColor: false,
        colorToAdd: globalIconTransformsInitialState.iconColors.activeColor,
        state: newState
    });

    newState = addToRecentColors({
        isBackgroundColor: true,
        colorToAdd:
            globalIconTransformsInitialState.backgroundColors.activeColor,
        state: newState
    });

    return newState;
};

export const formatHexCode = ({ hexCode }) => {
    if (!hexCode) {
        return "";
    } else {
        return `#${removeInvalidChars({ hexCode }).toUpperCase()}`;
    }

    function removeInvalidChars({ hexCode }) {
        return hexCode.replace(/[^0-9A-F]/gi, "");
    }
};

export const getActiveBackgroundColor = ({
    proposedColor,
    backgroundShape
}) => {
    const shapeIsSquare = backgroundShape === SHAPES.SQUARE;

    if (!proposedColor) {
        if (shapeIsSquare) {
            return "";
        } else {
            return DEFAULT_SHAPE_COLOR;
        }
    }

    return normalizeHexCode({ hexCode: proposedColor });
};

export const setShape = ({ state, shape }) => {
    let newState = {
        ...state,
        shape: shape,
        editCount: state.editCount + 1
    };

    const currentActiveBackgroundColor = state.backgroundColors.activeColor;
    newState.backgroundColors.activeColor = getActiveBackgroundColor({
        proposedColor: currentActiveBackgroundColor,
        backgroundShape: shape
    });

    return newState;
};

export const initializeEditor = ({
    iconId,
    persistedToken,
    persistedIconsColors,
    persistedBackgroundColors,
    persistedShape,
    state
}) => {
    const newState = {
        ...state,
        currentIconId: iconId,
        [iconId]: iconTransformsInitialState,
        token: persistedToken || iconEditorInitialState.token,
        iconColors: {
            ...state.iconColors,
            activeColor:
                persistedIconsColors && persistedIconsColors.length
                    ? persistedIconsColors[0]
                    : globalIconTransformsInitialState.iconColors.activeColor,
            recent:
                persistedIconsColors && persistedIconsColors.length
                    ? persistedIconsColors
                    : globalIconTransformsInitialState.iconColors.recent
        },
        backgroundColors: {
            ...state.backgroundColors,
            recent:
                persistedBackgroundColors && persistedBackgroundColors.length
                    ? persistedBackgroundColors
                    : globalIconTransformsInitialState.backgroundColors.recent
        },
        shape: persistedShape || globalIconTransformsInitialState.shape
    };

    const proposedActiveBackgroundColor =
        persistedBackgroundColors && persistedBackgroundColors.length
            ? persistedBackgroundColors[0]
            : globalIconTransformsInitialState.backgroundColors.activeColor;
    newState.backgroundColors.activeColor = getActiveBackgroundColor({
        proposedColor: proposedActiveBackgroundColor,
        backgroundShape: newState.shape
    });

    newState.editCount = iconIsEdited({ state: newState, iconId })
        ? globalIconTransformsInitialState.editCount + 1
        : globalIconTransformsInitialState.editCount;

    return newState;
};
